import {
  useEffect,
  useState,
  type ImgHTMLAttributes,
  type MouseEvent,
  type WheelEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { RotateCcw, RotateCw } from 'lucide-react'

export interface ZoomImageProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  /**
   * Optional flag to disable zoom behaviour while still using the
   * same styling wrapper.
   */
  disabled?: boolean
}

const MIN_SCALE = 1
const MAX_SCALE = 5
const SCALE_STEP = 0.25

function clampScale(value: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value))
}

export interface ZoomableImageProps {
  src?: string
  alt?: string
  /** Renders a Close button in the control cluster when provided. */
  onClose?: () => void
  /** Sizing for the image itself; hosts leaving room for footers override it. */
  imgClassName?: string
}

/**
 * The zoom/pan/rotate viewer shared by the single-photo preview and the
 * photo-group gallery: a top-right control cluster (zoom, quarter-turn
 * rotation, reset) plus the transformed image with wheel-zoom and drag-pan.
 *
 * Owns its transform state, so hosts reset it by remounting — the gallery
 * keys this by photo URL and gets a fresh view per photo for free. The host
 * provides the fixed backdrop; the control cluster anchors to it.
 */
export function ZoomableImage({
  src,
  alt,
  onClose,
  imgClassName = 'max-h-[88vh] max-w-[95vw]',
}: ZoomableImageProps) {
  const [scale, setScale] = useState(1)
  /** View-only rotation of the zoomed image, in quarter turns (0-3). */
  const [quarterTurns, setQuarterTurns] = useState(0)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  const handleWheelZoom = (event: WheelEvent<HTMLImageElement>) => {
    event.preventDefault()
    const direction = event.deltaY > 0 ? -1 : 1
    setScale((current) => clampScale(current + direction * SCALE_STEP))
  }

  const handleMouseDown = (event: MouseEvent<HTMLImageElement>) => {
    event.preventDefault()
    setIsDragging(true)
    setDragStart({
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    })
  }

  const handleMouseMove = (event: MouseEvent<HTMLImageElement>) => {
    if (!isDragging) return
    event.preventDefault()
    setOffset({
      x: event.clientX - dragStart.x,
      y: event.clientY - dragStart.y,
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  return (
    <>
      <div
        className="absolute right-4 top-4 z-20 flex items-center gap-2 rounded-md bg-black/45 p-1 backdrop-blur-sm"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="rounded-md bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
          onClick={() => {
            setScale((current) => {
              const next = clampScale(current - SCALE_STEP)
              if (next <= 1) {
                setOffset({ x: 0, y: 0 })
              }
              return next
            })
          }}
        >
          −
        </button>
        <span className="min-w-12 text-center text-xs text-white">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          className="rounded-md bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
          onClick={() => {
            setScale((current) => clampScale(current + SCALE_STEP))
          }}
        >
          +
        </button>
        <button
          type="button"
          className="rounded-md bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
          aria-label="Rotate left"
          title="Rotate left"
          onClick={() => {
            setQuarterTurns((current) => (current + 3) % 4)
          }}
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="rounded-md bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
          aria-label="Rotate right"
          title="Rotate right"
          onClick={() => {
            setQuarterTurns((current) => (current + 1) % 4)
          }}
        >
          <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="rounded-md bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
          onClick={() => {
            setScale(1)
            setQuarterTurns(0)
            setOffset({ x: 0, y: 0 })
          }}
        >
          Reset
        </button>
        {onClose ? (
          <button
            type="button"
            className="rounded-md bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
            onClick={onClose}
          >
            Close
          </button>
        ) : null}
      </div>

      <img
        src={src}
        alt={alt}
        data-zoom-image="true"
        className={`relative z-0 select-none object-contain ${imgClassName}`}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) rotate(${quarterTurns * 90}deg) scale(${scale})`,
          transformOrigin: 'center center',
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        onClick={(event) => event.stopPropagation()}
        onWheel={handleWheelZoom}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </>
  )
}

export function ZoomImage(props: ZoomImageProps) {
  const { disabled, className, alt, src, ...imgProps } = props
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  const baseImage = (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      {...imgProps}
      src={src}
      alt={alt}
      data-zoom-image="true"
      className={[className, !disabled ? 'cursor-zoom-in' : null]
        .filter(Boolean)
        .join(' ')}
    />
  )

  if (disabled || !src) {
    return baseImage
  }

  return (
    <>
      <button
        type="button"
        className="block w-full"
        onClick={() => setIsOpen(true)}
        aria-label="Open image zoom"
      >
        {baseImage}
      </button>

      {/* Portal to <body>: hosts (side panels, sticky tables) create their
          own stacking contexts, which would trap the overlay's z-index. */}
      {isOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4"
              onClick={() => setIsOpen(false)}
              role="dialog"
              aria-modal="true"
              aria-label="Image zoom preview"
            >
              {/* Mounted fresh per open, so the transform starts reset. */}
              <ZoomableImage
                src={src}
                alt={alt}
                onClose={() => setIsOpen(false)}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
